/*
 *   File : upsert-roles.js
 *   Author : https://evoqins.com
 *   Description : upsert roles component
 *   Integrations : sub-admin/modules-data, sub-admin/roles/add, sub-admin/roles/edit, sub-admin/roles/details
 *   Version : 1.0.2
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  setNavLink,
  setPageTitle,
  setSearchEnabled,
} from "../../Redux/Ui/ui.slice";
import { CheckboxSelect, CustomSelectMenu, Input } from "../../Components/FormElements";
import { PrimaryButton } from "../../Components/Buttons";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { APIService } from "../../Service";
import { Loader } from "../../Components/Other";

function normalizeSelectedOptions(role) {
  if (!role) return [];
  if (Array.isArray(role)) return role;
  if (typeof role === "object") return [role];
  return [];
}

export default function UpsertRoles() {
  const { id } = useParams();
  const navigate = useNavigate();
  //Redux title and searchbar setting
  const dispatch = useDispatch();

  // ---------------------------
  // FORM STATE
  // ---------------------------
  const [name, setName] = useState("");
  const [role, setRole] = useState([]);
  const [moduleOptions, setModuleOptions] = useState([]);

  // ---------------------------
  // LOADER STATE
  // ---------------------------
  const [loader, setLoader] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);

  // ---------------------------
  // ERROR STATE
  // ---------------------------
  const [nameError, setNameError] = useState("");
  const [roleError, setRoleError] = useState("");

  useEffect(() => {
    if (id) dispatch(setPageTitle(["Manage Roles", "Edit Role"]));
    else dispatch(setPageTitle(["Manage Roles", "Add Role"]));
    dispatch(setNavLink(["/manage-sub-admin"]));
    dispatch(setSearchEnabled(false));

    // Fetch module options
    _fetchModuleOptions();

    // If editing, fetch role details
    if (id) {
      _fetchRoleDetails();
    }
  }, [id]);

  // ---------------------------
  // FETCH MODULE OPTIONS
  // ---------------------------
  const _fetchModuleOptions = () => {
    APIService(false, "sub-admin/modules-data")
      .then((res) => {
        if (res.status_code === 200) {
          setModuleOptions(res.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching module options:", error);
        toast.error("Failed to load module options");
      });
  };

  // ---------------------------
  // FETCH ROLE DETAILS (FOR EDIT)
  // ---------------------------
  const _fetchRoleDetails = () => {
    setLoader(true);

    const payload = {
      role_id: parseInt(id),
    };

    APIService(true, "sub-admin/roles/detail", payload)
      .then((res) => {
        if (res.status_code === 200 && res.data) {
          const roleData = res.data;

          setName(roleData.name || "");
          setRole(
            roleData.modules?.map((module) => ({
              label: module.name,
              value: module.module_id,
            })) || [],
          );
        }
      })
      .catch((error) => {
        console.error("Error fetching role details:", error);
        toast.error("Failed to load role details");
      })
      .finally(() => {
        setLoader(false);
      });
  };

  // ---------------------------
  // VALIDATION HANDLER
  // ---------------------------
  const _handleSubmit = () => {
    let isValid = true;

    // NAME
    if (name.trim().length === 0) {
      setNameError("Name is required");
      isValid = false;
    }

    // ROLE MODULES
    if (normalizeSelectedOptions(role).length === 0) {
      setRoleError("Select at least one module");
      isValid = false;
    }

    if (!isValid) return;

    // Call upsert API
    _submitForm();
  };

  // ---------------------------
  // SUBMIT FORM (ADD/EDIT API)
  // ---------------------------
  const _submitForm = () => {
    setSubmitLoader(true);

    // Extract module IDs from selected roles
    const moduleIds = normalizeSelectedOptions(role).map((r) => r.value);

    let payload;
    let endpoint;

    if (id) {
      // Edit endpoint
      endpoint = "sub-admin/roles/edit";
      payload = {
        role_id: parseInt(id),
        name: name,
        modules: moduleIds,
      };
    } else {
      // Add endpoint
      endpoint = "sub-admin/roles/add";
      payload = {
        name: name,
        modules: moduleIds,
      };
    }

    APIService(true, endpoint, payload)
      .then((res) => {
        if (res.status_code === 200) {
          toast.success(
            res.message ||
              (id ? "Role updated successfully" : "Role added successfully"),
          );
          navigate("/manage-sub-admin");
        } else {
          toast.error(res.message || "Failed to save role");
        }
      })
      .catch((error) => {
        console.error("Error submitting form:", error);
        toast.error("Failed to save role");
      })
      .finally(() => {
        setSubmitLoader(false);
      });
  };

  // Show loader while fetching data
  if (loader) {
    return <Loader />;
  }

  return (
    <div className="row">
      <div className="col-12">
        <div className="e-glass-card rounded-16 d-flex flex-column p-24 bg-transparent">
          <div className="e-glass-card rounded-16 d-flex flex-column p-24">
            <h3 className="fw-500 fs-18 lh-20 text-eerie-black">
              {id ? "Edit" : "Add"} Role
            </h3>

            <div className="row">
              <div className="col-xl-6 col-lg-7 col-md-9 col-12">
                <div className="d-flex flex-column pt-3">
                  {/* NAME */}
                  <Input
                    id="name"
                    type="text"
                    label="Role Name"
                    placeholder="Enter role name"
                    require={true}
                    value={name}
                    error={nameError}
                    className="w-100 px-3 py-10"
                    handleChange={(v) => {
                      setName(v);
                      setNameError("");
                    }}
                    onKeyPress={_handleSubmit}
                    autoFocus={true}
                  />

                  {/* <div className="d-flex pb-2 w-100"> */}
                    {/* MULTI SELECT */}
                    {/* <CustomSelectMenu
                      label="Module Access"
                      options={moduleOptions}
                      placeholder="Select Modules"
                      require={true}
                      isMulti={true}
                      value={role}
                      error={roleError}
                      onChange={(selected) => {
                        setRole(selected);
                        setRoleError("");
                      }}
                    /> */}
                  {/* </div> */}

                  <div className="d-flex flex-column pt-1 mb-2">
                    <p className="mb-0 fw-400 fs-14 pb-1">
                      Module Access <span className="text-cg-red">*</span>
                    </p>

                    <CheckboxSelect
                      options={moduleOptions}
                      placeholder="Select Module"
                      require={true}
                      isMulti={true}
                      value={role}
                      error={roleError}
                      onChange={(selected) => {
                        setRole(selected);
                        setRoleError("");
                      }}
                    />
                  </div>

                  {/* BUTTONS */}
                  <div className="row row-cols-2 pt-24">
                    <div className="col">
                      <PrimaryButton
                        name="Cancel"
                        ClassName="rounded-pill bg-transparent border-oxford-blue lh-24 py-3 text-oxford-blue w-100 fs-16 fw-500"
                        onPress={() => navigate("/manage-sub-admin")}
                        disabled={submitLoader}
                      />
                    </div>
                    <div className="col">
                      <PrimaryButton
                        name={
                          submitLoader ? "Saving..." : id ? "Update" : "Add"
                        }
                        ClassName="rounded-pill bg-oxford-blue lh-24 py-3 text-white w-100 fs-16 fw-500"
                        onPress={_handleSubmit}
                        disabled={submitLoader}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
